'use client';

import { Button, DeprecatedLinkButton as LinkButton, Text } from '@lmn/design-system';
import { type HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import customCss from '@/app/components/hotel-detail/css/customCss';
import BasicModalHeader from '@/app/components/search/components/Modal/LayoutElements/BasicModalHeader';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import { useTranslation } from '@/app/components/translations/Translation';
import { formatNumber } from '@/utils/numberUtils.ts';
import type { Brand } from '@/domainLogic/index.ts';
import { useIsMobile } from '../hooks/useMediaSSR';
import TripAdvisorBubbles from './TripAdvisorBubbles';
import TripAdvisorOwl, { TripAdvisorAttribution } from './TripAdvisorOwl';
import TripAdvisorScore from './TripAdvisorScore';
import TripAdvisorReviewsModalContent from './TripAdvisorReviewsModalContent';
import { getTARatingInterval, toTADisplayScore } from './tripadvisor.helper';

const Container = styled.div`
  background-color: ${({ theme }) => theme.commons.colors.gray0};
  padding: 16px;
  border-radius: 8px;

  @media ${customCss.breakpoints.sm} {
    padding: 24px;
  }
`;

// Two-column summary (matches the reviews modal): score block on the left with a divider,
// category grid on the right. Stacks to a single column on mobile.
const SummaryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${customCss.breakpoints.sm} {
    flex-direction: row;
    align-items: flex-start;
    gap: 32px;
  }
`;

const ScoreBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const ScoreInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BubblesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
`;

// Right column of the summary: fills the space beside the score block (matches the modal).
// The divider lives on this column (not the score block) so it spans the full row height even
// though the score block is shorter and top-aligned.
const Categories = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  flex: 1;

  @media ${customCss.breakpoints.sm} {
    gap: 16px 32px;
    padding-left: 32px;
    border-left: 1px solid ${({ theme }) => theme.commons.colors.gray100};
  }
`;

const Category = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CategoryProgressBar = styled.div<{ $score: number }>`
  background-color: ${({ theme }) => theme.commons.colors.gray100};
  border-radius: 4px;
  height: 8px;
  position: relative;
  flex: 1;

  &::after {
    background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundSuccessContrast};
    border-radius: 4px;
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: ${({ $score }) => $score}%;
  }
`;

const BottomContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
`;

// Attribution row, top-right, desktop only (mirrors the reviews modal header). Hidden on mobile —
// the text + logo would overflow the narrow card; the owl in the BubblesRow keeps TA branding there.
const HeaderRow = styled.div`
  display: none;

  @media ${customCss.breakpoints.sm} {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
  }
`;

type TripAdvisorReviewsSectionProps = {
  hotelName: string;
  hotelOpinion: HotelOpinion;
  showMoreInfoEnabled: boolean;
  reviewsSectionTitleId: string;
  /** Render the full reviews list inline (Evo details modal) instead of a teaser + "view more" modal. */
  inline?: boolean;
} & HTMLAttributes<HTMLElement>;

export default function TripAdvisorReviewsSection({
  hotelName,
  hotelOpinion,
  showMoreInfoEnabled,
  reviewsSectionTitleId,
  inline = false,
}: TripAdvisorReviewsSectionProps) {
  const { staticData, commonData, localization } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { t } = useTranslation();
  const { locale } = localization;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const hasAutoOpenedRef = useRef(false);

  const rawScore = hotelOpinion.rating;
  const reviewCount = hotelOpinion.numberOfReviews ?? 0;
  const ratingInterval = rawScore ? getTARatingInterval(rawScore) : undefined;
  const reviewCountFormatted = formatNumber({ locale, number: reviewCount });
  // Aggregate category breakdown reuses the same field as TrustYou (hotelOpinion.categories, 0–100);
  // Supply repopulates it with TripAdvisor categories on cutover.
  const categories = hotelOpinion.categories ?? [];

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  useEffect(() => {
    if (!hasAutoOpenedRef.current && window.location.hash === '#reviews') {
      hasAutoOpenedRef.current = true;
      openModal();
    }
  }, [openModal]);

  const title = isMobile ? t('vacation.select.navigation.reviews') : t('vacation.select.reviews.hotel', { hotelName });

  if (!rawScore) return null;

  // Evo details modal: reviews render inline (full header + categories + list, no "view more" modal).
  if (inline) {
    return <TripAdvisorReviewsModalContent inline={true} />;
  }

  return (
    <>
      <Container>
        <HeaderRow>
          <TripAdvisorAttribution />
        </HeaderRow>

        <SummaryRow>
          <ScoreBlock>
            <TripAdvisorScore rawScore={rawScore} size="large" />
            <ScoreInfo>
              {ratingInterval && <Text variant="Title4">{t(ratingInterval.translationKey)}</Text>}
              <BubblesRow>
                <TripAdvisorOwl />
                <TripAdvisorBubbles rawScore={rawScore} css="font-size: 14px;" />
                <Text variant="Caption" color="textSecondary">
                  ({reviewCountFormatted})
                </Text>
              </BubblesRow>
            </ScoreInfo>
          </ScoreBlock>

          {categories.length > 0 && (
            <Categories>
              {categories.map((category) => (
                <Category key={category.name}>
                  <Text variant="Body" weight="bold" color="textPrimary">
                    {category.name}
                  </Text>
                  <CategoryBarRow>
                    <CategoryProgressBar $score={category.score} />
                    <Text
                      variant="BodySmall"
                      color="textPrimary"
                      css="flex-shrink: 0; min-width: 28px; text-align: left;"
                    >
                      {toTADisplayScore(category.score)}
                    </Text>
                  </CategoryBarRow>
                </Category>
              ))}
            </Categories>
          )}
        </SummaryRow>

        <BottomContainer>
          {showMoreInfoEnabled &&
            (isMobile ? (
              <Button
                variant="primary"
                appearance="outlined"
                onClick={openModal}
                aria-labelledby={`view-more-reviews-text ${reviewsSectionTitleId}`}
                css={`
                  width: 100%;
                `}
              >
                <span id="view-more-reviews-text">{t('vacation.select.details.review.viewmore')}</span>
              </Button>
            ) : (
              <LinkButton
                variant="accent"
                onClick={openModal}
                aria-labelledby={`view-more-reviews-text ${reviewsSectionTitleId}`}
                css={css`
                  ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
                `}
              >
                <span id="view-more-reviews-text">{t('vacation.select.details.review.viewmore')}</span>
              </LinkButton>
            ))}
        </BottomContainer>
      </Container>

      <OpenXModal
        isOpen={isModalOpen}
        isMobile={isMobile ?? true}
        brand={staticData.domainValues?.brand as Brand}
        onClose={closeModal}
        onCloseByBrowserHistory={closeModal}
        onOpenByBrowserHistory={openModal}
        fullScreen={!isMobile}
        className={isMobile ? 'hotel-reviews-modal' : 'hotel-reviews-modal hotel-modal-lateral-evo'}
        id="hotel-reviews-section-modal"
        aria-label={title}
        bodyBackgroundColor="backgroundPage"
        header={{
          sticky: true,
          element: <BasicModalHeader handleClose={closeModal}>{title}</BasicModalHeader>,
        }}
      >
        <TripAdvisorReviewsModalContent />
      </OpenXModal>
    </>
  );
}
